import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiClock, FiDownload, FiTrash2, FiX, FiRefreshCw } from 'react-icons/fi';
import { Node, Edge } from 'reactflow';
import ConfirmDialog from './ConfirmDialog';

interface Version {
  id: number;
  projectId: string;
  projectName: string;
  architectureId?: string;
  architectureName?: string;
  version: string;
  description: string;
  nodes: Node[];
  edges: Edge[];
  createdAt: string;
}

interface VersionHistoryProps {
  architectureId: string;
  architectureName: string;
  onLoadVersion: (nodes: Node[], edges: Edge[]) => void;
  onClose: () => void;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({ architectureId, architectureName, onLoadVersion, onClose }) => {
  const { t } = useTranslation('canvas');
  const [versions, setVersions] = useState<Version[]>([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmLoad, setShowConfirmLoad] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState<Version | null>(null);
  const [versionToLoad, setVersionToLoad] = useState<Version | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadVersions();
  }, [architectureId]);

  const loadVersions = () => {
    const allVersions = JSON.parse(localStorage.getItem('project.versions') || '[]');
    // Filtrer par architectureId (nouveau système) ou par architectureName (ancien système)
    const architectureVersions = allVersions.filter((v: Version) =>
      v.architectureId === architectureId ||
      (!v.architectureId && v.architectureName === architectureName)
    );
    // Sort by creation date, newest first
    architectureVersions.sort((a: Version, b: Version) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    setVersions(architectureVersions);
  };

  const handleLoadVersion = (version: Version) => {
    setVersionToLoad(version);
    setShowConfirmLoad(true);
  };

  const confirmLoadVersion = () => {
    if (!versionToLoad) return;
    onLoadVersion(versionToLoad.nodes, versionToLoad.edges);
    setSuccessMessage(`Version "${versionToLoad.version}" chargée avec succès`);
    setTimeout(() => setSuccessMessage(null), 3000);
    setShowConfirmLoad(false);
    setVersionToLoad(null);
    onClose();
  };

  const handleDeleteVersion = (version: Version) => {
    setVersionToDelete(version);
    setShowConfirmDelete(true);
  };

  const confirmDeleteVersion = () => {
    if (!versionToDelete) return;
    const allVersions = JSON.parse(localStorage.getItem('project.versions') || '[]');
    const updated = allVersions.filter((v: Version) => v.id !== versionToDelete.id);
    localStorage.setItem('project.versions', JSON.stringify(updated));
    loadVersions();
    setSuccessMessage(`Version "${versionToDelete.version}" supprimée avec succès`);
    setTimeout(() => setSuccessMessage(null), 3000);
    setShowConfirmDelete(false);
    setVersionToDelete(null);
  };

  const handleExportVersion = (version: Version) => {
    const name = version.architectureName || version.projectName;
    const data = {
      version: version.version,
      description: version.description,
      architectureName: name,
      nodes: version.nodes,
      edges: version.edges,
      createdAt: version.createdAt,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${name}-${version.version}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          width: '90%',
          maxWidth: 800,
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <FiClock size={24} style={{ color: '#3B82F6' }} />
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#111827' }}>
                {t('versionHistory.title')}
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6B7280' }}>
                {architectureName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              display: 'flex',
              alignItems: 'center',
              borderRadius: 6,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#F3F4F6')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <FiX size={20} style={{ color: '#6B7280' }} />
          </button>
        </div>

        <div style={{ padding: 24, overflowY: 'auto', maxHeight: 'calc(80vh - 80px)' }}>
          {versions.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: 60,
                color: '#9CA3AF',
              }}
            >
              <FiClock size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
              <p style={{ fontSize: 16, margin: 0 }}>{t('versionHistory.empty.title')}</p>
              <p style={{ fontSize: 14, margin: '8px 0 0', color: '#D1D5DB' }}>
                {t('versionHistory.empty.description')}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {versions.map((version) => (
                <div
                  key={version.id}
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    padding: 16,
                    background: '#FAFAFA',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3B82F6';
                    e.currentTarget.style.background = '#F9FAFB';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.background = '#FAFAFA';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>
                        {version.version}
                      </h3>
                      {version.description && (
                        <p style={{ margin: '4px 0 0', fontSize: 14, color: '#6B7280' }}>
                          {version.description}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleLoadVersion(version)}
                        style={{
                          padding: '6px 12px',
                          background: '#3B82F6',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                        title={t('versionHistory.buttons.loadTitle')}
                      >
                        <FiRefreshCw size={14} />
                        {t('versionHistory.buttons.load')}
                      </button>
                      <button
                        onClick={() => handleExportVersion(version)}
                        style={{
                          padding: '6px 12px',
                          background: 'white',
                          color: '#374151',
                          border: '1px solid #D1D5DB',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                        title={t('versionHistory.buttons.exportTitle')}
                      >
                        <FiDownload size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteVersion(version)}
                        style={{
                          padding: '6px 12px',
                          background: 'white',
                          color: '#EF4444',
                          border: '1px solid #FCA5A5',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                        title={t('versionHistory.buttons.deleteTitle')}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#9CA3AF' }}>
                    <span>📅 {new Date(version.createdAt).toLocaleString('fr-FR')}</span>
                    <span>📦 {version.nodes.length} {t('versionHistory.stats.resources')}</span>
                    <span>🔗 {version.edges.length} {t('versionHistory.stats.connections')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={showConfirmDelete}
        title="Supprimer cette version ?"
        message={`Êtes-vous sûr de vouloir supprimer la version "${versionToDelete?.version}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={confirmDeleteVersion}
        onCancel={() => {
          setShowConfirmDelete(false);
          setVersionToDelete(null);
        }}
      />

      <ConfirmDialog
        isOpen={showConfirmLoad}
        title="Charger cette version ?"
        message={`Voulez-vous charger la version "${versionToLoad?.version}" ? Les modifications non sauvegardées seront perdues.`}
        confirmText="Charger"
        cancelText="Annuler"
        variant="warning"
        onConfirm={confirmLoadVersion}
        onCancel={() => {
          setShowConfirmLoad(false);
          setVersionToLoad(null);
        }}
      />

      {/* Success Toast Notification */}
      {successMessage && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: '#10b981',
            color: '#fff',
            padding: '16px 24px',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            zIndex: 1100,
            animation: 'slideInUp 0.3s ease-out',
            maxWidth: 400,
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <span style={{ fontSize: 14, fontWeight: 500 }}>{successMessage}</span>
        </div>
      )}

      <style>
        {`
          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default VersionHistory;
